import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { Label } from "./annotate";

/**
 * Anivaze v4 — GEOMETRY actor. Geometry is the MOST drawable topic on earth, and
 * it was rendering as boxes-on-a-dotted-rail (the generic flow chip). This draws
 * the ACTUAL figure for every shapes/geometry beat — a point, a line with
 * arrowheads extending both ways, an angle whose ray sweeps to show degrees, a
 * triangle with its angle arcs, the Pythagorean squares-on-the-sides diagram,
 * quadrilaterals, circles, polygons — all as pure functions of `t` (seconds), so
 * they animate continuously and never freeze.
 *
 * Director drives it via items[0] = the subject, parsed from the beat text by
 * parseGeometryConfig (mirrors the engine/bio/circuit actors).
 */

const TAU = Math.PI * 2;
const D2R = Math.PI / 180;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

export type GeoSubject =
  | "point" | "line" | "segment" | "ray" | "angle" | "right-angle"
  | "triangle" | "quadrilateral" | "pythagoras" | "circle" | "polygon";

export interface GeometryConfig { subject: GeoSubject }

export function parseGeometryConfig(text: string): GeometryConfig {
  const s = text.toLowerCase();
  // specific figures outrank generic words ("right angle" before "angle",
  // "triangle" before "line", etc.)
  const subject: GeoSubject =
    /pythagor|hypotenuse|a²|c²|a2 ?\+ ?b2/.test(s) ? "pythagoras"
    : /right angle|right-angle|perpendicular|90 ?degrees?|90°/.test(s) ? "right-angle"
    : /triangle/.test(s) ? "triangle"
    : /quadrilateral|rectangle|rhombus|parallelogram|trapezoid|four[- ]sided|four sides|4 sides|\bsquare\b/.test(s) ? "quadrilateral"
    : /circle|radius|diameter|circumference|\barc\b/.test(s) ? "circle"
    : /\bangle|\bdegree|acute|obtuse|rotation|protractor/.test(s) ? "angle"
    : /\bsegments?\b/.test(s) ? "segment"
    : /\brays?\b/.test(s) ? "ray"
    : /pentagon|hexagon|heptagon|octagon|polygon|\bsides\b/.test(s) ? "polygon"
    : /\blines?\b/.test(s) ? "line"
    : "point";
  return { subject };
}

/** Geometry signal — route shapes/geometry beats to the real figure, not a rail. */
export const GEOMETRY_RE =
  /\bgeometr|\bpoints?\b|\blines?\b|\brays?\b|\bsegments?\b|\bangles?\b|\bdegrees?\b|\btriangles?\b|\bquadrilateral|\brectangles?\b|\bpolygons?\b|\bpentagons?\b|\bhexagons?\b|pythagor|hypotenuse|\bvertex\b|\bvertices\b|perpendicular|\bparallel\b|\bradius\b|\bdiameter\b|circumference|acute|obtuse/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

// ── small helpers ───────────────────────────────────────────────────────────
const ptOn = (cx: number, cy: number, r: number, deg: number) => ({
  x: cx + Math.cos(deg * D2R) * r,
  y: cy + Math.sin(deg * D2R) * r,
});
const arcPath = (cx: number, cy: number, r: number, a0: number, a1: number) => {
  const p0 = ptOn(cx, cy, r, a0), p1 = ptOn(cx, cy, r, a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  const sweep = a1 > a0 ? 1 : 0;
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} ${sweep} ${p1.x} ${p1.y}`;
};
const ArrowHead: React.FC<{ x: number; y: number; deg: number; s: number; color: string }> = ({ x, y, deg, s, color }) => {
  const a = deg * D2R;
  return <path d={`M ${x} ${y} L ${x - s * Math.cos(a - 0.4)} ${y - s * Math.sin(a - 0.4)} L ${x - s * Math.cos(a + 0.4)} ${y - s * Math.sin(a + 0.4)} Z`} fill={color} />;
};

// ── POINT — a dot with a pulsing "no size" magnifier ─────────────────────────
const Point: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy;
  const pulse = 0.5 + 0.5 * Math.sin(t * 2);
  return (
    <g>
      {/* zoom rings emphasising it has no size */}
      {[0, 1, 2].map((i) => {
        const rr = ((t * 0.6 + i / 3) % 1);
        return <circle key={i} cx={cx} cy={cy} r={u * (2 + rr * 12)} fill="none" stroke={P.primary} strokeWidth={u * 0.25} opacity={0.35 * (1 - rr)} />;
      })}
      <circle cx={cx} cy={cy} r={u * (1.4 + 0.3 * pulse)} fill={P.primary} />
      <Label tx={cx} ty={cy} lx={cx + 9 * u} ly={cy - 5 * u} text="Point" theme={theme} u={u} color={P.primary} />
      <text x={cx} y={cy + 12 * u} fill={P.sub} fontSize={u * 1.8} fontWeight={700} textAnchor="middle" fontFamily={theme.body.family}>no width · no height · no area</text>
    </g>
  );
};

// ── LINE — arrowheads both ends, dashes flowing outward (extends forever) ─────
const Line: React.FC<VProps & { u: number; segment?: boolean; ray?: boolean }> = ({ box, theme, t, u, segment, ray }) => {
  const P = theme.palette;
  const cy = box.cy, cx = box.cx;
  const halfW = box.w * 0.42;
  const x0 = cx - halfW, x1 = cx + halfW;
  const flow = (t * 6) % (u * 4); // outward dash motion
  const ax = cx - halfW * 0.5, bx = cx + halfW * 0.5; // the two named points
  return (
    <g>
      <line x1={x0} y1={cy} x2={x1} y2={cy} stroke={P.ink} strokeWidth={u * 0.4}
        strokeDasharray={`${u * 1.6} ${u * 1.6}`} strokeDashoffset={-flow} opacity={0.9} />
      {/* solid core between the points for a segment */}
      {segment && <line x1={ax} y1={cy} x2={bx} y2={cy} stroke={P.primary} strokeWidth={u * 0.55} />}
      {!segment && <ArrowHead x={x1} y={cy} deg={0} s={u * 2.2} color={P.ink} />}
      {!segment && !ray && <ArrowHead x={x0} y={cy} deg={180} s={u * 2.2} color={P.ink} />}
      {ray && <ArrowHead x={x1} y={cy} deg={0} s={u * 2.2} color={P.ink} />}
      {/* named endpoints */}
      <circle cx={ax} cy={cy} r={u * 1.1} fill={P.primary} />
      <circle cx={bx} cy={cy} r={u * 1.1} fill={P.primary} />
      <text x={ax} y={cy - 3 * u} fill={P.ink} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>{ray ? "Start" : "A"}</text>
      <text x={bx} y={cy - 3 * u} fill={P.ink} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>B</text>
      <text x={cx} y={cy + 9 * u} fill={P.sub} fontSize={u * 1.8} fontWeight={700} textAnchor="middle" fontFamily={theme.body.family}>
        {segment ? "segment — a piece with two endpoints" : ray ? "ray — starts at a point, goes on forever" : "line — no endpoints, extends both ways"}
      </text>
    </g>
  );
};

// ── ANGLE — vertex + baseline + a sweeping ray, arc & degree readout ─────────
const Angle: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const vx = box.cx - 6 * u, vy = box.cy + 8 * u;
  const R = Math.min(box.w, box.h) * 0.34;
  const deg = (t * 40) % 360;              // sweeps continuously (measures rotation)
  const arcR = R * 0.32;
  const base = ptOn(vx, vy, R, 0);         // baseline ray →
  const arm = ptOn(vx, vy, R, -deg);       // sweeping ray (screen-y is down, so negate)
  const markCol = deg > 88 && deg < 92 ? P.positive : P.primary;
  return (
    <g>
      {/* quadrant guide dots at 0/90/180/270 */}
      {[0, 90, 180, 270].map((d) => {
        const p = ptOn(vx, vy, R, -d);
        return <g key={d}><circle cx={p.x} cy={p.y} r={u * 0.5} fill={P.muted} /><text x={p.x} y={p.y - u} fill={P.sub} fontSize={u * 1.4} textAnchor="middle" fontFamily={theme.body.family}>{d}°</text></g>;
      })}
      <line x1={vx} y1={vy} x2={base.x} y2={base.y} stroke={P.ink} strokeWidth={u * 0.4} />
      <line x1={vx} y1={vy} x2={arm.x} y2={arm.y} stroke={markCol} strokeWidth={u * 0.5} />
      <path d={arcPath(vx, vy, arcR, 0, -deg)} fill="none" stroke={markCol} strokeWidth={u * 0.4} />
      <circle cx={vx} cy={vy} r={u * 1} fill={P.ink} />
      <text x={vx + arcR + 2 * u} y={vy - arcR} fill={markCol} fontSize={u * 3} fontWeight={800} fontFamily={theme.body.family}>{Math.round(deg)}°</text>
      <Label tx={vx} ty={vy} lx={vx - 2 * u} ly={vy + 6 * u} text="Vertex" theme={theme} u={u} anchor="end" />
    </g>
  );
};

// ── RIGHT ANGLE — perpendicular rays + corner square + 90° ───────────────────
const RightAngle: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const vx = box.cx - 8 * u, vy = box.cy + 8 * u;
  const L = Math.min(box.w, box.h) * 0.4;
  const sq = u * 3;
  const pop = clamp01(t / 0.8);           // corner square snaps in
  const pulse = 0.6 + 0.4 * Math.sin(t * 3);
  return (
    <g>
      <line x1={vx} y1={vy} x2={vx + L} y2={vy} stroke={P.ink} strokeWidth={u * 0.5} />
      <ArrowHead x={vx + L} y={vy} deg={0} s={u * 1.8} color={P.ink} />
      <line x1={vx} y1={vy} x2={vx} y2={vy - L} stroke={P.ink} strokeWidth={u * 0.5} />
      <ArrowHead x={vx} y={vy - L} deg={-90} s={u * 1.8} color={P.ink} />
      <rect x={vx} y={vy - sq} width={sq * pop} height={sq * pop} fill="none" stroke={P.positive} strokeWidth={u * 0.4} />
      <circle cx={vx} cy={vy} r={u * 1} fill={P.ink} />
      <text x={vx + sq + 3 * u} y={vy - sq} fill={P.positive} fontSize={u * 2.8} fontWeight={800} fontFamily={theme.body.family} opacity={pulse}>90°</text>
      <text x={box.cx} y={vy + 7 * u} fill={P.sub} fontSize={u * 1.8} fontWeight={700} textAnchor="middle" fontFamily={theme.body.family}>a right angle — a perfect square corner</text>
    </g>
  );
};

// ── TRIANGLE — drawn sides, angle arcs, "angles sum to 180°" ─────────────────
const Triangle: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy;
  const R = Math.min(box.w, box.h) * 0.36;
  const A = ptOn(cx, cy, R, -90);          // top
  const B = ptOn(cx, cy, R, 150);          // bottom-left
  const C = ptOn(cx, cy, R, 30);           // bottom-right
  const draw = clamp01(t / 1.6);
  const travel = (t * 0.25) % 1;           // a dot walks the perimeter (continuous)
  const peri = [A, B, C, A];
  const seg = Math.floor(travel * 3), fr = travel * 3 - seg;
  const p = { x: peri[seg].x + (peri[seg + 1].x - peri[seg].x) * fr, y: peri[seg].y + (peri[seg + 1].y - peri[seg].y) * fr };
  const arc = (V: { x: number; y: number }, U: { x: number; y: number }, W: { x: number; y: number }, col: string) => {
    const a1 = Math.atan2(U.y - V.y, U.x - V.x) / D2R;
    const a2 = Math.atan2(W.y - V.y, W.x - V.x) / D2R;
    return <path d={arcPath(V.x, V.y, u * 3, a1, a2)} fill="none" stroke={col} strokeWidth={u * 0.35} />;
  };
  return (
    <g>
      <path d={`M ${A.x} ${A.y} L ${B.x} ${B.y} L ${C.x} ${C.y} Z`} fill={P.primary} fillOpacity={0.1}
        stroke={P.primary} strokeWidth={u * 0.55} pathLength={1} strokeDasharray={1} strokeDashoffset={1 - draw} />
      {draw > 0.98 && <>{arc(A, B, C, P.danger)}{arc(B, C, A, P.positive)}{arc(C, A, B, P.highlight)}</>}
      {draw > 0.98 && <circle cx={p.x} cy={p.y} r={u * 0.8} fill={P.primary} />}
      <text x={A.x} y={A.y - 2 * u} fill={P.ink} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>A</text>
      <text x={B.x - 2 * u} y={B.y + 3 * u} fill={P.ink} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>B</text>
      <text x={C.x + 2 * u} y={C.y + 3 * u} fill={P.ink} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>C</text>
      <text x={cx} y={cy + R + 6 * u} fill={P.sub} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>∠A + ∠B + ∠C = 180°</text>
    </g>
  );
};

// ── PYTHAGORAS — right triangle with a² + b² = c² squares on the sides ───────
const Pythagoras: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const s = Math.min(box.w, box.h) * 0.2;
  // right angle at R; legs a (vertical) and b (horizontal), hypotenuse c
  const R = { x: box.cx - s * 0.6, y: box.cy + s * 0.5 };
  const top = { x: R.x, y: R.y - s };            // a leg up
  const right = { x: R.x + s * 1.4, y: R.y };    // b leg right
  const a = s, b = s * 1.4;
  const draw = clamp01(t / 1.2);
  const fill = clamp01((t - 1.2) / 1.2);         // squares fill after the triangle draws
  return (
    <g>
      {/* triangle */}
      <path d={`M ${R.x} ${R.y} L ${top.x} ${top.y} L ${right.x} ${right.y} Z`} fill="none"
        stroke={P.ink} strokeWidth={u * 0.5} pathLength={1} strokeDasharray={1} strokeDashoffset={1 - draw} />
      {/* right-angle marker */}
      <rect x={R.x} y={R.y - u * 2} width={u * 2} height={u * 2} fill="none" stroke={P.positive} strokeWidth={u * 0.3} />
      {/* square on leg a (to the LEFT of the vertical leg) */}
      <rect x={R.x - a} y={R.y - a} width={a} height={a} fill={P.primary} fillOpacity={0.18 + 0.22 * fill} stroke={P.primary} strokeWidth={u * 0.35} />
      <text x={R.x - a / 2} y={R.y - a / 2 + u} fill={P.primary} fontSize={u * 2.4} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>a²</text>
      {/* square on leg b (BELOW the horizontal leg) */}
      <rect x={R.x} y={R.y} width={b} height={b} fill={P.highlight} fillOpacity={0.18 + 0.22 * fill} stroke={P.highlight} strokeWidth={u * 0.35} />
      <text x={R.x + b / 2} y={R.y + b / 2 + u} fill={P.highlight} fontSize={u * 2.4} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>b²</text>
      {/* square on hypotenuse c (built outward from the slope) */}
      {(() => {
        const dx = right.x - top.x, dy = right.y - top.y;              // hypotenuse vector
        const nx = dy / Math.hypot(dx, dy), ny = -dx / Math.hypot(dx, dy); // outward normal
        const c = Math.hypot(dx, dy);
        const p1 = top, p2 = right;
        const p3 = { x: p2.x + nx * c, y: p2.y + ny * c };
        const p4 = { x: p1.x + nx * c, y: p1.y + ny * c };
        const mid = { x: (p1.x + p2.x + p3.x + p4.x) / 4, y: (p1.y + p2.y + p3.y + p4.y) / 4 };
        return <g>
          <path d={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z`} fill={P.danger} fillOpacity={0.18 + 0.22 * fill} stroke={P.danger} strokeWidth={u * 0.35} />
          <text x={mid.x} y={mid.y + u} fill={P.danger} fontSize={u * 2.4} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>c²</text>
        </g>;
      })()}
      <text x={box.cx} y={box.y + box.h - 3 * u} fill={P.ink} fontSize={u * 2.4} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>a² + b² = c²</text>
    </g>
  );
};

// ── QUADRILATERAL — a four-sided shape with a perimeter tracer ────────────────
const Quadrilateral: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const w = Math.min(box.w, box.h) * 0.34, h = w * 0.72;
  const cx = box.cx, cy = box.cy;
  const V = [
    { x: cx - w, y: cy - h }, { x: cx + w, y: cy - h },
    { x: cx + w, y: cy + h }, { x: cx - w, y: cy + h },
  ];
  const draw = clamp01(t / 1.4);
  const travel = (t * 0.25) % 1;
  const loop = [...V, V[0]];
  const seg = Math.floor(travel * 4), fr = travel * 4 - seg;
  const p = { x: loop[seg].x + (loop[seg + 1].x - loop[seg].x) * fr, y: loop[seg].y + (loop[seg + 1].y - loop[seg].y) * fr };
  return (
    <g>
      <path d={`M ${V[0].x} ${V[0].y} L ${V[1].x} ${V[1].y} L ${V[2].x} ${V[2].y} L ${V[3].x} ${V[3].y} Z`}
        fill={P.primary} fillOpacity={0.1} stroke={P.primary} strokeWidth={u * 0.55}
        pathLength={1} strokeDasharray={1} strokeDashoffset={1 - draw} />
      {V.map((v, i) => <circle key={i} cx={v.x} cy={v.y} r={u * 0.9} fill={P.primary} />)}
      {draw > 0.98 && <circle cx={p.x} cy={p.y} r={u * 0.8} fill={P.danger} />}
      <text x={cx} y={cy + h + 6 * u} fill={P.sub} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>four sides · four vertices</text>
    </g>
  );
};

// ── CIRCLE — a sweeping radius traces the circle, r/diameter labeled ──────────
const Circle: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy;
  const r = Math.min(box.w, box.h) * 0.34;
  const trace = (t * 0.35) % 1;            // radius sweeps → traces the circle
  const deg = -trace * 360;
  const tip = ptOn(cx, cy, r, deg);
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={P.primary} fillOpacity={0.08} stroke={P.primary} strokeWidth={u * 0.5}
        pathLength={1} strokeDasharray={1} strokeDashoffset={1 - clamp01(trace + 0.02)} transform={`rotate(-90 ${cx} ${cy})`} />
      <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke={P.danger} strokeWidth={u * 0.45} />
      <circle cx={cx} cy={cy} r={u * 0.9} fill={P.ink} />
      <circle cx={tip.x} cy={tip.y} r={u * 0.8} fill={P.danger} />
      <text x={(cx + tip.x) / 2} y={(cy + tip.y) / 2 - u} fill={P.danger} fontSize={u * 2} fontWeight={800} fontFamily={theme.body.family}>r</text>
      <Label tx={cx} ty={cy} lx={cx} ly={cy + r + 5 * u} text="centre" theme={theme} u={u} anchor="middle" />
    </g>
  );
};

// ── POLYGON — a regular n-gon ─────────────────────────────────────────────────
const Polygon: React.FC<VProps & { u: number; n: number }> = ({ box, theme, t, u, n }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy;
  const r = Math.min(box.w, box.h) * 0.34;
  const spin = t * 6;
  const V = Array.from({ length: n }, (_, i) => ptOn(cx, cy, r, -90 + spin + (360 / n) * i));
  const d = V.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ") + " Z";
  return (
    <g>
      <path d={d} fill={P.primary} fillOpacity={0.12} stroke={P.primary} strokeWidth={u * 0.55} />
      {V.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={u * 0.8} fill={P.primary} />)}
      <text x={cx} y={cy + r + 6 * u} fill={P.sub} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>{n} sides · {n} vertices</text>
    </g>
  );
};

const polygonSides = (text: string): number =>
  /triangle|\b3 ?sides/.test(text) ? 3 : /pentagon|\b5 ?sides/.test(text) ? 5
  : /hexagon|\b6 ?sides/.test(text) ? 6 : /heptagon|\b7 ?sides/.test(text) ? 7
  : /octagon|\b8 ?sides/.test(text) ? 8 : 6;

export const GeometryActor: React.FC<VProps> = (props) => {
  const { box, items } = props;
  const cfg = parseGeometryConfig(items.join(" "));
  const u = Math.min(box.h / 38, box.w / 34);
  const p = { ...props, u };
  switch (cfg.subject) {
    case "point": return <Point {...p} />;
    case "line": return <Line {...p} />;
    case "segment": return <Line {...p} segment />;
    case "ray": return <Line {...p} ray />;
    case "angle": return <Angle {...p} />;
    case "right-angle": return <RightAngle {...p} />;
    case "triangle": return <Triangle {...p} />;
    case "quadrilateral": return <Quadrilateral {...p} />;
    case "pythagoras": return <Pythagoras {...p} />;
    case "circle": return <Circle {...p} />;
    case "polygon": return <Polygon {...p} n={polygonSides(items.join(" "))} />;
    default: return <Point {...p} />;
  }
};
